import React, { useState } from 'react';

const sections = [
  {
    id: 'overview',
    title: 'Overview',
    content: (
      <div className="space-y-3">
        <p>LiveRundown is a browser-based live sports production rundown system. It lets you manage show cues, automate vMix switching, trigger Singular.live graphics, and keep the whole production team in sync — all from a web browser.</p>
        <p>Multiple users can be connected to the same show simultaneously. All changes (cue edits, status updates, reorders) are broadcast in real time to every connected client.</p>
      </div>
    ),
  },
  {
    id: 'dashboard',
    title: 'Show Dashboard',
    content: (
      <div className="space-y-3">
        <p>The dashboard lists all shows in your workspace. Each card shows the show name, sport type, scheduled start time, and current status.</p>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-[#2a2a2a]">
              <th className="text-left py-1.5 pr-4 text-gray-400 font-medium w-24">Status</th>
              <th className="text-left py-1.5 text-gray-400 font-medium">Meaning</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['DRAFT', 'Show is being built; not yet ready to go live'],
              ['READY', 'Rundown is finalised and the team is standing by'],
              ['LIVE', 'Show is currently on air'],
              ['COMPLETE', 'Show has ended'],
            ].map(([s, d]) => (
              <tr key={s}>
                <td className="py-1.5 pr-4 font-mono text-xs text-white">{s}</td>
                <td className="py-1.5 text-gray-300">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>Click a show card to open the rundown editor. Use the <span className="kbd">+ New Show</span> button to create a new show.</p>
      </div>
    ),
  },
  {
    id: 'views',
    title: 'Views',
    content: (
      <div className="space-y-4">
        <p>The rundown editor has five views selectable from the tab bar below the header. Each view is optimised for a different role. Press the number key to switch instantly.</p>
        <div className="space-y-3">
          {[
            {
              key: '1',
              name: 'Producer View',
              desc: 'Full rundown table with all columns visible. Inline cue editing, drag-and-drop reordering, TAKE/BACK/SKIP controls, and show clock. This is the default view for the show caller.',
            },
            {
              key: '2',
              name: 'TD View',
              desc: 'Simplified rundown focused on vMix inputs. Shows large ON AIR and NEXT panels at the top, a tally strip displaying which vMix input is live, and manual trigger buttons for each pending cue. Read-only.',
            },
            {
              key: '3',
              name: 'Graphics View',
              desc: 'Filtered to show GRAPHIC cues and their Singular.live compositions. Large TAKE ON / TAKE OFF buttons per cue, an ad hoc graphics panel, and a CLEAR ALL emergency button. Read-only.',
            },
            {
              key: '4',
              name: 'Solo View',
              desc: 'Consolidated dashboard combining clock, TAKE control, condensed rundown, mini tally strip, and quick-trigger buttons for the next three graphics. Designed for one-person productions.',
            },
            {
              key: '5',
              name: 'Raw View',
              desc: 'Spreadsheet-style table with click-to-edit cells. Shows all cues with Time In/Out columns (wall-clock scheduling), cue fields, and logo/graphics columns. Supports paste from Excel or Google Sheets. Drag-and-drop reordering available.',
            },
            {
              key: '6',
              name: 'Singular View',
              desc: 'Preview and monitor Singular.live graphics. Embeds a Preview output and a Live output side by side as iframes. A separate preview token lets you pull the GFX composition list and fire graphics into the preview output to check them before committing to air.',
            },
          ].map(v => (
            <div key={v.key} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#2a2a2a] rounded text-center text-xs font-mono text-white leading-6">{v.key}</span>
              <div>
                <p className="text-white font-medium text-sm">{v.name}</p>
                <p className="text-gray-400 text-sm mt-0.5">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'cues',
    title: 'Cues',
    content: (
      <div className="space-y-4">
        <p>A cue is a single item in the rundown — one production event or segment. Cues are shown as rows in the rundown table.</p>

        <h3 className="text-white font-medium">Cue Fields</h3>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-[#2a2a2a]">
              <th className="text-left py-1.5 pr-4 text-gray-400 font-medium w-36">Field</th>
              <th className="text-left py-1.5 text-gray-400 font-medium">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['Label', 'Short display name shown in the rundown row (max 80 characters)'],
              ['Type', 'Cue category — determines default colour coding and intended use'],
              ['Duration', 'Planned duration in seconds (0 is allowed for section breaks / info rows)'],
              ['Script', 'Free-form notes or script visible to the Producer only'],
              ['Notes', 'Operator notes visible to all roles'],
              ['Colour', 'Optional hex colour to override the default type colour'],
              ['vMix Action', 'One or more vMix commands to execute when this cue goes on air'],
              ['Singular Action', 'A Singular.live composition to show or hide when this cue goes on air'],
            ].map(([f, d]) => (
              <tr key={f}>
                <td className="py-1.5 pr-4 text-white text-xs font-medium">{f}</td>
                <td className="py-1.5 text-gray-300 text-sm">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="text-white font-medium">Cue Types</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          {[
            ['SEGMENT', 'grey', 'A show segment (pre-show, half-time, interview, etc.)'],
            ['VT', 'green', 'A video playout item'],
            ['VT_VOX', 'green', 'A video playout item with live voiceover — shares VT’s green'],
            ['REPLAY', 'lime', 'Replay or highlight moment — playout, so green-family'],
            ['CAMERA', 'blue', 'A vMix camera input cut'],
            ['PRESENTERS_TO_CAMERA', 'pink', 'Presenters live to camera'],
            ['GRAPHIC', 'purple', 'A Singular.live graphic trigger'],
            ['AUDIO', 'magenta', 'An audio mix change'],
            ['GROUP', 'amber', 'A container grouping child cues'],
            ['PRE_SHOW', 'yellow', 'Runs before the show start point'],
            ['SECTION_BREAK', 'white band', 'A hard divider in the rundown'],
          ].map(([t, c, d]) => (
            <div key={t} className="flex gap-2">
              <span className="flex-shrink-0 font-mono text-xs text-white w-32">{t}</span>
              <span className="text-gray-400 text-xs">{d}</span>
            </div>
          ))}
        </div>

        <h3 className="text-white font-medium">Cue Status</h3>
        <table className="w-full text-sm border-collapse">
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['PENDING', 'Not yet executed — default state'],
              ['ON AIR', 'Currently active. Highlighted in red in the rundown'],
              ['COMPLETE', 'Has been executed. Dimmed in the rundown'],
              ['SKIPPED', 'Was skipped. Shown with strikethrough text'],
            ].map(([s, d]) => (
              <tr key={s}>
                <td className="py-1.5 pr-4 font-mono text-xs text-white w-24">{s}</td>
                <td className="py-1.5 text-gray-300">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="text-white font-medium">Adding &amp; Editing Cues</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
          <li>Click <strong className="text-white">+ Add Cue</strong> or press <kbd className="kbd">N</kbd> to add a cue at the end of the rundown.</li>
          <li>Click any cell in a cue row to edit it inline. Press <kbd className="kbd">Enter</kbd> or click away to save. Changes are auto-saved within 500 ms.</li>
          <li>Right-click a cue row for a context menu with Duplicate, Delete, and Skip options.</li>
          <li>Drag the handle on the left of a row to reorder. Cues that are ON AIR or COMPLETE cannot be moved.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'controls',
    title: 'Show Controls',
    content: (
      <div className="space-y-4">
        <p>The main show controls appear in the sub-header bar below the clock. They are always visible regardless of scroll position.</p>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-[#2a2a2a]">
              <th className="text-left py-1.5 pr-4 text-gray-400 font-medium w-24">Control</th>
              <th className="text-left py-1.5 pr-4 text-gray-400 font-medium w-16">Key</th>
              <th className="text-left py-1.5 text-gray-400 font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['TAKE', 'Space', 'Advances to the next pending cue, marks the current cue as complete, and fires any attached vMix / Singular actions on the new cue.'],
              ['BACK', 'B', 'Steps back one cue. Prompts for confirmation if the previous cue had automation attached.'],
              ['SKIP', 'S', 'Marks the selected cue as skipped and advances to the next pending cue.'],
              ['START SHOW', '—', 'Transitions the show status to LIVE and starts the show elapsed clock. Appears before the show goes live.'],
              ['END SHOW', '—', 'Transitions the show status to COMPLETE. Requires confirmation.'],
            ].map(([c, k, d]) => (
              <tr key={c}>
                <td className="py-1.5 pr-4 font-medium text-white text-xs">{c}</td>
                <td className="py-1.5 pr-4 font-mono text-xs text-amber-400">{k}</td>
                <td className="py-1.5 text-gray-300">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: 'clock',
    title: 'Show Clock & Segment Timer',
    content: (
      <div className="space-y-3">
        <p>The clock panel is docked in the header and is always visible.</p>
        <table className="w-full text-sm border-collapse">
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['Show Start', 'Scheduled start time. Click to edit (enter HH:MM or HH:MM:SS). Used as the anchor for Time In/Out calculations in the Raw view.'],
              ['Wall Clock', 'Current local time in HH:MM:SS.'],
              ['Show Elapsed', 'Time since START SHOW was pressed, counting up.'],
              ['Show Remaining', 'Scheduled duration minus elapsed. Goes red when under 5 minutes.'],
              ['Segment Timer', 'Counts down the planned duration of the current ON AIR cue. Goes amber at 30 s, flashes red when over time (shown as +00:15 style).'],
            ].map(([f, d]) => (
              <tr key={f}>
                <td className="py-2 pr-4 text-white text-xs font-medium w-36 align-top">{f}</td>
                <td className="py-2 text-gray-300 text-sm">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-sm text-gray-400">Press <kbd className="kbd">R</kbd> to reset the segment timer without advancing the cue.</p>
      </div>
    ),
  },
  {
    id: 'rawview',
    title: 'Raw View — Spreadsheet Mode',
    content: (
      <div className="space-y-3">
        <p>The Raw view (tab <kbd className="kbd">5</kbd>) shows all cues as a spreadsheet-style table. It is designed for fast bulk editing and scheduling checks.</p>

        <h3 className="text-white font-medium">Columns</h3>
        <table className="w-full text-sm border-collapse">
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['#', 'Row number (read-only)'],
              ['Time In', 'Calculated wall-clock time when this cue starts, based on Show Start and the cumulative durations of all preceding cues. Read-only.'],
              ['Time Out', 'Calculated wall-clock time when this cue ends. Read-only.'],
              ['Label', 'Cue name — click to edit'],
              ['Duration', 'Planned duration in seconds — click to edit (0 allowed)'],
              ['Type', 'Cue type dropdown — click to edit'],
              ['Script', 'Script/notes for the producer — click to edit'],
              ['Notes', 'Operator notes — click to edit'],
              ['TR Logo', 'Top-right logo graphic identifier — click to edit'],
              ['BR Logo', 'Bottom-right logo graphic identifier — click to edit'],
              ['Other GFX', 'Other graphics notes — click to edit'],
            ].map(([c, d]) => (
              <tr key={c}>
                <td className="py-1.5 pr-4 text-white text-xs font-medium w-24">{c}</td>
                <td className="py-1.5 text-gray-300 text-sm">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="text-white font-medium">Pasting from Excel or Google Sheets</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
          <li>Click a cell to select it, then paste (<kbd className="kbd">Ctrl+V</kbd> / <kbd className="kbd">Cmd+V</kbd>).</li>
          <li>Pasted data fills downward from the selected cell, matching the column order: Label → Duration → Type → Script → Notes → TR Logo → BR Logo → Other GFX.</li>
          <li>Empty cells in the clipboard write a dash (<code className="text-amber-400">-</code>) to the app, preserving row positions. Blank rows in a multi-row paste are not skipped.</li>
          <li>Pasting a single value into a single cell works normally (no multi-cell paste behaviour).</li>
          <li>Reorder rows by dragging the handle on the left of each row.</li>
        </ul>

        <h3 className="text-white font-medium">Multi-select Delete</h3>
        <p className="text-sm text-gray-300">Check the checkbox on the left of one or more rows, then click the red <strong className="text-white">Delete Selected</strong> button that appears in the toolbar.</p>
      </div>
    ),
  },
  {
    id: 'vmix',
    title: 'vMix Integration',
    content: (
      <div className="space-y-3">
        <p>vMix is controlled via its HTTP API. The LiveRundown backend proxies all commands — vMix only needs to be reachable from the backend server, not from your browser.</p>

        <h3 className="text-white font-medium">Connection Setup</h3>
        <p className="text-sm text-gray-300">Open Show Settings (gear icon in the header) and enter the vMix host IP and port (default: 8088). Click <strong className="text-white">Connect</strong> to test the connection. The status indicator in the header shows the current state:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400 text-sm">
          <li><span className="text-green-400">Green dot</span> — Connected</li>
          <li><span className="text-amber-400">Amber dot</span> — Connecting / retrying</li>
          <li><span className="text-red-400">Red dot</span> — Disconnected (banner warning shown)</li>
        </ul>
        <p className="text-sm text-gray-300">If vMix disconnects during a show, the backend retries every 3 seconds. The rundown remains fully functional for manual operation.</p>

        <h3 className="text-white font-medium">Attaching vMix Actions to Cues</h3>
        <p className="text-sm text-gray-300">In the cue editor, add one or more vMix actions. Each action has:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400 text-sm">
          <li><strong className="text-white">Function</strong> — the vMix command (e.g. Cut, Fade, SetVolume)</li>
          <li><strong className="text-white">Input</strong> — the vMix input number or name</li>
          <li><strong className="text-white">Value</strong> — optional parameter (e.g. volume level)</li>
          <li><strong className="text-white">Delay (ms)</strong> — optional delay before this action fires, allowing staggered automation</li>
        </ul>
        <p className="text-sm text-gray-300">Actions execute sequentially in order when the cue goes ON AIR via TAKE.</p>

        <h3 className="text-white font-medium">Supported vMix Commands</h3>
        <div className="grid grid-cols-2 gap-x-6 text-sm">
          <div>
            <p className="text-gray-400 uppercase text-xs tracking-wider mb-1">Switching</p>
            <ul className="space-y-0.5 text-gray-300">
              <li>Cut — hard cut to input</li>
              <li>Fade — dissolve to input</li>
              <li>CutDirect — cut preview to program</li>
              <li>PreviewInput — set preview</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-400 uppercase text-xs tracking-wider mb-1">Audio</p>
            <ul className="space-y-0.5 text-gray-300">
              <li>SetVolume — set input volume</li>
              <li>SetMute — mute / unmute</li>
              <li>SetMasterVolume — master volume</li>
              <li>AudioBusOn / AudioBusOff</li>
            </ul>
          </div>
          <div className="mt-3">
            <p className="text-gray-400 uppercase text-xs tracking-wider mb-1">Record &amp; Stream</p>
            <ul className="space-y-0.5 text-gray-300">
              <li>StartRecording / StopRecording</li>
              <li>StartStreaming / StopStreaming</li>
            </ul>
          </div>
        </div>

        <h3 className="text-white font-medium">Tally</h3>
        <p className="text-sm text-gray-300">The backend subscribes to vMix's TCP tally feed. The TD view displays a live tally strip showing which input is in program (red) and which is in preview (green).</p>
      </div>
    ),
  },
  {
    id: 'singular',
    title: 'Singular.live Integration',
    content: (
      <div className="space-y-3">
        <p>Singular.live graphics are controlled via the Singular REST API. API keys are stored server-side and never exposed to the browser.</p>

        <h3 className="text-white font-medium">Connection Setup</h3>
        <p className="text-sm text-gray-300">In Show Settings, enter your Singular.live API Key and Output ID. The backend will fetch the composition list for that output and cache it. Use <strong className="text-white">Refresh Compositions</strong> to re-sync if you update your Singular project mid-show.</p>

        <h3 className="text-white font-medium">Attaching Singular Actions to Cues</h3>
        <p className="text-sm text-gray-300">In the cue editor, each cue can have one Singular action:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400 text-sm">
          <li><strong className="text-white">Show</strong> — takes a composition on air, optionally updating its data fields first</li>
          <li><strong className="text-white">Hide</strong> — takes a composition off air</li>
        </ul>
        <p className="text-sm text-gray-300">Select the composition from the dropdown, optionally add data field key/value pairs, then save. The action fires automatically when the cue is taken on air.</p>

        <h3 className="text-white font-medium">Graphics View Controls</h3>
        <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
          <li>Each GRAPHIC cue shows a <strong className="text-white">TAKE ON</strong> and <strong className="text-white">TAKE OFF</strong> button for manual control.</li>
          <li>The ad hoc panel lets you trigger any composition independently of the rundown.</li>
          <li><strong className="text-white">CLEAR ALL GRAPHICS</strong> hides all currently visible compositions — requires a double-click to confirm.</li>
        </ul>

        <h3 className="text-white font-medium">On-Air Status</h3>
        <p className="text-sm text-gray-300">Compositions that are currently on air are indicated with a green badge. Status is polled from the Singular API after each trigger action.</p>
      </div>
    ),
  },
  {
    id: 'import',
    title: 'Importing Cues',
    content: (
      <div className="space-y-3">
        <p>You can import a rundown from a CSV file or a public Google Sheet. The import appends to any existing cues by default; you can choose to replace all.</p>

        <h3 className="text-white font-medium">CSV Import</h3>
        <p className="text-sm text-gray-300">Upload a <code className="text-amber-400">.csv</code> file. A downloadable template is available from the import dialog.</p>

        <h3 className="text-white font-medium">Google Sheets Import</h3>
        <p className="text-sm text-gray-300">Paste a Google Sheets URL. The sheet must be set to <em>Anyone with the link can view</em>. The <strong className="text-white">Re-sync from Sheets</strong> button re-imports from the same URL at any time — new rows are appended and existing rows are updated, but vMix and Singular action configurations on existing cues are preserved.</p>

        <h3 className="text-white font-medium">Required Columns</h3>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-[#2a2a2a]">
              <th className="text-left py-1.5 pr-4 text-gray-400 font-medium w-28">Column</th>
              <th className="text-left py-1.5 pr-4 text-gray-400 font-medium w-20">Required</th>
              <th className="text-left py-1.5 text-gray-400 font-medium">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['label', 'Yes', 'Cue display name'],
              ['type', 'Yes', 'Must match a cue type exactly (e.g. SEGMENT, CAMERA)'],
              ['duration', 'Yes', 'Integer seconds. 0 is allowed.'],
              ['script', 'No', 'Producer script/notes'],
              ['notes', 'No', 'Operator notes'],
              ['colour', 'No', 'Hex colour code (e.g. #ff0000)'],
            ].map(([c, r, n]) => (
              <tr key={c}>
                <td className="py-1.5 pr-4 font-mono text-xs text-amber-400">{c}</td>
                <td className="py-1.5 pr-4 text-xs text-gray-400">{r}</td>
                <td className="py-1.5 text-gray-300 text-sm">{n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: 'collaboration',
    title: 'Multi-User Collaboration',
    content: (
      <div className="space-y-3">
        <p>Multiple users can be connected to the same show simultaneously. All rundown changes are broadcast in real time via WebSocket to every connected client.</p>
        <p>LiveRundown uses a <strong className="text-white">last-write-wins</strong> model — if two users edit the same cue field at the same time, the last save wins. Each cue row shows a <em>Last edited by [user]</em> indicator.</p>
        <h3 className="text-white font-medium">Roles</h3>
        <table className="w-full text-sm border-collapse">
          <tbody className="divide-y divide-[#1a1a1a]">
            {[
              ['Admin', 'Full access — create shows, manage users, all controls'],
              ['Producer', 'Edit rundown, trigger all actions, manage show clock'],
              ['TD', 'View rundown, trigger vMix actions only'],
              ['Graphics', 'View rundown, trigger Singular.live actions only'],
              ['Viewer', 'Read-only — no trigger capability'],
            ].map(([r, d]) => (
              <tr key={r}>
                <td className="py-1.5 pr-4 text-white text-xs font-medium w-24">{r}</td>
                <td className="py-1.5 text-gray-300">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: 'shortcuts',
    title: 'Keyboard Shortcuts',
    content: (
      <div className="space-y-2">
        <p className="text-sm text-gray-400">Keyboard shortcuts work anywhere in the rundown editor, except when a text input is focused.</p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm mt-3">
          {[
            ['Space', 'TAKE — advance to next cue'],
            ['B', 'BACK — step back one cue'],
            ['S', 'SKIP — skip selected cue'],
            ['R', 'Reset segment timer'],
            ['N', 'Add new cue at end'],
            ['Del', 'Delete selected cues (confirms first; Enter accepts)'],
            ['1', 'Switch to Producer view'],
            ['2', 'Switch to TD view'],
            ['3', 'Switch to Graphics view'],
            ['4', 'Switch to Solo view'],
            ['5', 'Switch to Raw view'],
            ['6', 'Switch to Singular view'],
            ['?', 'Toggle shortcut help panel'],
            ['Escape', 'Cancel inline edit'],
          ].map(([k, d]) => (
            <div key={k} className="flex items-center gap-3">
              <kbd className="kbd w-16 text-center flex-shrink-0">{k}</kbd>
              <span className="text-gray-300">{d}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: 'settings',
    title: 'Show Settings',
    content: (
      <div className="space-y-3">
        <p>Open Show Settings by clicking the gear icon in the top-right corner of the header.</p>
        <p>From here you can configure:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
          <li>Show name, sport type, and scheduled start time / duration</li>
          <li>vMix connection (host IP and port) — includes a connection test button</li>
          <li>Singular.live API key and output ID</li>
          <li>Google Sheets URL for rundown import / re-sync</li>
          <li>Input discovery — refresh the vMix input list if inputs change pre-show</li>
        </ul>
        <p className="text-sm text-gray-400">The show start time can also be edited inline by clicking the time display in the header clock panel.</p>
      </div>
    ),
  },
];

export default function HelpPage() {
  const [active, setActive] = useState('overview');

  const activeSection = sections.find(s => s.id === active);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200 flex flex-col">
      {/* Top bar */}
      <div className="bg-[#111111] border-b border-[#2a2a2a] px-6 h-11 flex items-center gap-3 flex-shrink-0">
        <div className="w-5 h-5 bg-red-600 rounded flex items-center justify-center">
          <div className="w-2 h-2 bg-white rounded-full" />
        </div>
        <span className="font-semibold text-white text-sm">LiveRundown</span>
        <span className="text-[#2a2a2a]">|</span>
        <span className="text-gray-400 text-sm">Help &amp; Reference</span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar nav */}
        <nav className="w-52 flex-shrink-0 bg-[#0f0f0f] border-r border-[#2a2a2a] overflow-y-auto py-4">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                active === s.id
                  ? 'text-white bg-[#1a1a1a] border-l-2 border-red-500'
                  : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
              }`}
            >
              {s.title}
            </button>
          ))}
        </nav>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-8 max-w-3xl">
          {activeSection && (
            <>
              <h2 className="text-xl font-semibold text-white mb-4">{activeSection.title}</h2>
              <div className="text-gray-300 leading-relaxed">
                {activeSection.content}
              </div>
            </>
          )}
        </main>
      </div>

      <style>{`
        .kbd {
          display: inline-block;
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 4px;
          padding: 1px 6px;
          font-family: monospace;
          font-size: 0.75rem;
          color: #e5e7eb;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
}
